import { CartAddEvent } from '@theme/events';

/**
 * Custom <product-form> element for the StickyGolfPro PDP.
 * Submits the form to /cart/add.js via AJAX, then dispatches CartAddEvent
 * so Horizon's <cart-drawer-component auto-open> opens and the cart bubble updates.
 *
 * The legacy Dawn-era markup defines `<product-form>` (not Horizon's `<product-form-component>`)
 * and disables the variant id input until JS enables it on submit — we preserve that contract.
 */
class StickyProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;

    this.submitButton = this.form.querySelector('[type="submit"][name="add"]');
    this.errorWrapper = this.querySelector('.product-form__error-message-wrapper');
    this.errorMessage = this.querySelector('.product-form__error-message');

    this.form.addEventListener('submit', this.handleSubmit);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.handleSubmit);
  }

  handleSubmit = async (event) => {
    event.preventDefault();

    if (this.submitButton?.getAttribute('aria-disabled') === 'true') return;

    this.toggleLoading(true);
    this.hideError();

    const variantInput = this.form.querySelector('input[name="id"]');
    if (variantInput) variantInput.disabled = false;

    // Collect any checked bonus-product toggles (bonus balls, club wedge, etc.)
    const additionalVariantIds = Array.from(
      document.querySelectorAll('[data-additional-product]:checked')
    ).map((cb) => parseInt(cb.dataset.additionalProductId, 10)).filter(Boolean);

    // Bug B fix: also collect the buy-buttons upsell checkbox (#upsellProductCheckbox).
    // It uses name="upsellId" which Shopify ignores in FormData — only Buy Now read it.
    // Push its variant ID here so the JSON multi-item branch adds it alongside the main product.
    try {
      const upsellCb = document.querySelector('#upsellProductCheckbox:checked');
      if (upsellCb) {
        const upsellVariantId = parseInt(upsellCb.value, 10);
        if (upsellVariantId) additionalVariantIds.push(upsellVariantId);
      }
    } catch (e) { /* non-blocking — missing checkbox must not break ATC */ }

    const sectionIds = new Set();
    document.querySelectorAll('cart-items-component, cart-icon-component').forEach((el) => {
      const id = el.dataset?.sectionId;
      if (id) sectionIds.add(id);
    });
    const sectionsStr = Array.from(sectionIds).join(',');

    const rootUrl = (window.Shopify?.routes?.root || '/').replace(/\/+$/, '/');

    let response, data;

    try {
      if (additionalVariantIds.length > 0) {
        // Single JSON request bundles main product + all bonus items — one round trip
        const mainVariantId = parseInt(variantInput?.value, 10);
        const qty = parseInt(this.form.querySelector('[name="quantity"]')?.value || '1', 10);

        // Extract any custom line item properties from the form
        const properties = {};
        for (const [key, value] of new FormData(this.form).entries()) {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match) properties[match[1]] = value;
        }

        const sellingPlan = this.form.querySelector('[name="selling_plan"]')?.value;
        const mainItem = { id: mainVariantId, quantity: qty };
        if (Object.keys(properties).length) mainItem.properties = properties;
        if (sellingPlan) mainItem.selling_plan = sellingPlan;

        const items = [mainItem, ...additionalVariantIds.map((id) => ({ id, quantity: 1 }))];
        const body = { items };
        if (sectionsStr) body.sections = sectionsStr;
        body.sections_url = window.location.pathname;

        response = await fetch(`${rootUrl}cart/add.js`, {
          method: 'POST',
          headers: {
            'Accept': 'application/javascript',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body),
        });
      } else {
        // No bonus items — use FormData (preserves all form fields)
        const formData = new FormData(this.form);
        if (sectionsStr) formData.set('sections', sectionsStr);
        formData.set('sections_url', window.location.pathname);

        response = await fetch(`${rootUrl}cart/add.js`, {
          method: 'POST',
          headers: {
            'Accept': 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: formData,
        });
      }

      data = await response.json().catch(() => ({}));

      if (!response.ok || data.status) {
        this.showError(data.description || data.message || 'Could not add to cart.');
        document.dispatchEvent(
          new CartAddEvent({}, this.id || 'sticky-product-form', {
            didError: true,
            source: 'product-form-component',
          })
        );
        return;
      }

      const cartResponse = await fetch(`${rootUrl}cart.js`);
      const cart = await cartResponse.json();

      document.dispatchEvent(
        new CartAddEvent(cart, this.id || 'sticky-product-form', {
          source: 'product-form-component',
          itemCount: cart.item_count,
          sections: data.sections,
        })
      );

      // Post-ATC popup intercept: show complementary product recommendations
      // before opening the cart drawer. If no recs, falls through to drawer directly.
      // Gated by: (1) product metafield opt-in OR sgp_post_atc=1 query/UTM override
      // for previews, (2) Intelligems test variant if running. Override persists
      // for the session via sessionStorage so it survives page navigation.
      const productId = this.dataset?.productId;
      const popupMetafieldEnabled = this.dataset?.postAtcPopup === 'true';
      let previewOverride = false;
      try {
        const params = new URLSearchParams(window.location.search);
        if (params.get('sgp_post_atc') === '1' || params.get('utm_content') === 'sgp_post_atc') {
          sessionStorage.setItem('sgpPostATCPreview', '1');
        }
        previewOverride = sessionStorage.getItem('sgpPostATCPreview') === '1';
      } catch (_) {}
      const igTestRunning = typeof window.__sgpPostATCTest !== 'undefined';
      const igTestEnabled = !igTestRunning || window.__sgpPostATCTest === true;
      let popupShown = false;
      if (productId && (popupMetafieldEnabled || previewOverride) && igTestEnabled && window.sgpPostATC && typeof window.sgpPostATC.open === 'function') {
        popupShown = await window.sgpPostATC.open(productId);
      }

      if (!popupShown) {
        const drawer = document.querySelector('cart-drawer-component');
        if (drawer && typeof drawer.showDialog === 'function') {
          drawer.showDialog();
        }
      }
    } catch (err) {
      console.error('[sticky-product-form] add-to-cart failed:', err);
      this.showError('Could not add to cart. Please try again.');
    } finally {
      this.toggleLoading(false);
    }
  };

  toggleLoading(loading) {
    if (!this.submitButton) return;
    const spinner = this.submitButton.querySelector('.loading-overlay__spinner');
    if (loading) {
      this.submitButton.setAttribute('aria-disabled', 'true');
      spinner?.classList.remove('hidden');
    } else {
      this.submitButton.removeAttribute('aria-disabled');
      spinner?.classList.add('hidden');
    }
  }

  showError(message) {
    if (!this.errorWrapper) return;
    if (this.errorMessage) this.errorMessage.textContent = message;
    this.errorWrapper.hidden = false;
  }

  hideError() {
    if (this.errorWrapper) this.errorWrapper.hidden = true;
  }
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', StickyProductForm);
}
